/**
 * PartyPicker — inline person/company selector (epic #833, B1)
 *
 * The party-native replacement for the legacy customer autocomplete: a
 * person/company toggle, a debounced registry search and quick-create that
 * reuses the person/company form dialogs. Used for additional work-order
 * parties, orderer change, vehicle owner and email linking.
 *
 * Once the user has typed two characters, "create new person/company" rows are
 * appended to the results — they are the only rows when nothing matched — and
 * the create form opens prefilled from the typed text (#3641), like the
 * work-order wizard's unified search.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PersonFormDialog } from '@/components/persons/PersonFormDialog';
import { CompanyFormDialog } from '@/components/companies/CompanyFormDialog';
import { usePersons, type Person } from '@/hooks/usePersons';
import { useCompanies, type Company } from '@/hooks/useCompanies';
import { useDebounce } from '@/hooks/useDebounce';
import type { PersonFieldsData } from '@/components/persons/PersonFormFields';
import type { CompanyFieldsData } from '@/components/companies/CompanyFormFields';
import { companyPrefillFromSearch, personPrefillFromSearch } from './partySearchPrefill';

export type PartyType = 'person' | 'company';

/** A selected party reference with display fields. */
export interface PartyRef {
  partyType: PartyType;
  personId: string | null;
  companyId: string | null;
  name: string;
  phone: string | null;
}

export function personToPartyRef(person: Person): PartyRef {
  return {
    partyType: 'person',
    personId: person.id,
    companyId: null,
    name: `${person.firstName} ${person.lastName}`.trim(),
    phone: person.phone ?? null,
  };
}

export function companyToPartyRef(company: Company): PartyRef {
  return {
    partyType: 'company',
    personId: null,
    companyId: company.id,
    name: company.name,
    phone: company.phone ?? null,
  };
}

type PickerOption =
  | { kind: 'person'; person: Person }
  | { kind: 'company'; company: Company }
  | { kind: 'create'; partyType: PartyType; input: string };

function optionKey(option: PickerOption): string {
  switch (option.kind) {
    case 'person':
      return `person-${option.person.id}`;
    case 'company':
      return `company-${option.company.id}`;
    case 'create':
      return `create-${option.partyType}`;
  }
}

function optionLabel(option: PickerOption): string {
  switch (option.kind) {
    case 'person':
      return `${option.person.firstName} ${option.person.lastName}`;
    case 'company':
      return option.company.name;
    case 'create':
      return option.input;
  }
}

interface PartyPickerProps {
  onSelect: (party: PartyRef) => void;
  autoFocus?: boolean;
  /** Restrict which party types can be picked (default: both). */
  allowedTypes?: readonly PartyType[];
}

const BOTH_PARTY_TYPES: readonly PartyType[] = ['person', 'company'];

export function PartyPicker({ onSelect, autoFocus, allowedTypes = BOTH_PARTY_TYPES }: PartyPickerProps) {
  const { t } = useTranslation('workorders');

  const [partyType, setPartyType] = useState<PartyType>(allowedTypes[0] ?? 'person');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [personQuickOpen, setPersonQuickOpen] = useState(false);
  const [companyQuickOpen, setCompanyQuickOpen] = useState(false);
  // Held in state so the dialog sees a stable reference while it is open.
  const [personPrefill, setPersonPrefill] = useState<Partial<PersonFieldsData>>({});
  const [companyPrefill, setCompanyPrefill] = useState<Partial<CompanyFieldsData>>({});

  const enableSearch = debouncedSearch.length >= 2;
  const personsQuery = usePersons(
    { q: debouncedSearch || undefined },
    { enabled: enableSearch && partyType === 'person' },
  );
  const companiesQuery = useCompanies(
    { q: debouncedSearch || undefined },
    { enabled: enableSearch && partyType === 'company' },
  );
  const personResults = personsQuery.data?.pages.flatMap((p) => p.data);
  const companyResults = companiesQuery.data?.pages.flatMap((p) => p.data);
  const isLoading = partyType === 'person' ? personsQuery.isLoading : companiesQuery.isLoading;

  const liveQ = search.trim();
  const options: PickerOption[] = (() => {
    const hits: PickerOption[] = !enableSearch
      ? []
      : partyType === 'person'
        ? (personResults ?? []).map((person) => ({ kind: 'person', person }))
        : (companyResults ?? []).map((company) => ({ kind: 'company', company }));
    if (liveQ.length < 2) return hits;
    // The active type's create row first, then the other allowed type.
    const createTypes = [partyType, ...allowedTypes.filter((type) => type !== partyType)];
    const actions: PickerOption[] = createTypes.map((type) => ({ kind: 'create', partyType: type, input: liveQ }));
    return [...hits, ...actions];
  })();

  const openPersonCreate = (input: string) => {
    setPersonPrefill(personPrefillFromSearch(input));
    setPersonQuickOpen(true);
  };
  const openCompanyCreate = (input: string) => {
    setCompanyPrefill(companyPrefillFromSearch(input));
    setCompanyQuickOpen(true);
  };

  const handleSelect = (_event: React.SyntheticEvent, value: PickerOption | null) => {
    if (!value) return;
    switch (value.kind) {
      case 'person':
        onSelect(personToPartyRef(value.person));
        return;
      case 'company':
        onSelect(companyToPartyRef(value.company));
        return;
      case 'create':
        if (value.partyType === 'person') openPersonCreate(value.input);
        else openCompanyCreate(value.input);
        return;
    }
  };

  return (
    <Stack spacing={1.5}>
      {allowedTypes.length > 1 && (
        <ToggleButtonGroup
          value={partyType}
          exclusive
          fullWidth
          size="small"
          onChange={(_e, value: PartyType | null) => {
            if (value) {
              setPartyType(value);
              setSearch('');
            }
          }}
        >
          <ToggleButton value="person">{t('parties.person')}</ToggleButton>
          <ToggleButton value="company">{t('parties.company')}</ToggleButton>
        </ToggleButtonGroup>
      )}

      <Autocomplete<PickerOption>
        key={partyType}
        options={options}
        loading={isLoading}
        value={null}
        onChange={handleSelect}
        inputValue={search}
        onInputChange={(_e, value, reason) => {
          // Picking a row must not overwrite the typed text with its label.
          if (reason !== 'reset') setSearch(value);
        }}
        getOptionLabel={optionLabel}
        isOptionEqualToValue={(o, v) => optionKey(o) === optionKey(v)}
        filterOptions={(x) => x}
        renderOption={(props, option) => {
          const { key: _key, ...optionProps } = props as { key?: string } & React.HTMLAttributes<HTMLLIElement>;
          switch (option.kind) {
            case 'person':
              return (
                <li {...optionProps} key={optionKey(option)}>
                  <Box>
                    <Typography variant="body2">{option.person.firstName} {option.person.lastName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[option.person.phone, option.person.email].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </li>
              );
            case 'company':
              return (
                <li {...optionProps} key={optionKey(option)}>
                  <Box>
                    <Typography variant="body2">{option.company.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[option.company.businessId, option.company.phone].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </li>
              );
            case 'create':
              return (
                <li {...optionProps} key={optionKey(option)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                    <AddIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="primary">
                      {option.partyType === 'person'
                        ? t('create.createPersonAction', { name: option.input })
                        : t('create.createCompanyAction', { name: option.input })}
                    </Typography>
                  </Box>
                </li>
              );
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={partyType === 'person' ? t('parties.selectPerson') : t('parties.selectCompany')}
            autoFocus={autoFocus}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        noOptionsText={enableSearch ? t('create.noPartiesFound') : t('create.partySearchHint')}
      />

      <Box>
        {partyType === 'person' ? (
          <Button size="small" startIcon={<AddIcon />} onClick={() => openPersonCreate(search)}>
            {t('parties.quickCreatePerson')}
          </Button>
        ) : (
          <Button size="small" startIcon={<AddIcon />} onClick={() => openCompanyCreate(search)}>
            {t('parties.quickCreateCompany')}
          </Button>
        )}
      </Box>

      <PersonFormDialog
        open={personQuickOpen}
        onClose={() => setPersonQuickOpen(false)}
        onCreated={(person) => onSelect(personToPartyRef(person))}
        // Quick create stays short; address/notes sit behind Lisätiedot (#2904).
        showExtended={false}
        initialData={personPrefill}
      />
      <CompanyFormDialog
        open={companyQuickOpen}
        onClose={() => setCompanyQuickOpen(false)}
        onCreated={(company) => onSelect(companyToPartyRef(company))}
        initialData={companyPrefill}
      />
    </Stack>
  );
}
